import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material'
import {
  Announcement,
  CreateAnnouncementRequest,
  UpdateAnnouncementRequest,
  AnnouncementTargetRole,
  AnnouncementPriority,
  announcementService,
} from '../../services/announcementService'

interface AnnouncementFormDialogProps {
  open: boolean
  announcement: Announcement | null
  onClose: () => void
  onSubmit: () => void
}

export const AnnouncementFormDialog: React.FC<AnnouncementFormDialogProps> = ({
  open,
  announcement,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation('announcement')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [summary, setSummary] = useState('')
  const [targetRole, setTargetRole] = useState<AnnouncementTargetRole>('ALL')
  const [priority, setPriority] = useState<AnnouncementPriority>('LOW')
  const [isPinned, setIsPinned] = useState(false)
  const [scheduledAt, setScheduledAt] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [emailNotification, setEmailNotification] = useState(false)

  const isEdit = !!announcement

  // ISO文字列をローカル時刻のdatetime-local形式に変換
  const convertToLocalDateTimeString = (isoString: string | undefined): string => {
    if (!isoString) return ''
    const date = new Date(isoString)
    // ローカルタイムゾーンでの YYYY-MM-DDTHH:mm 形式に変換
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  useEffect(() => {
    if (announcement) {
      setTitle(announcement.title)
      setContent(announcement.content)
      setSummary(announcement.summary || '')
      setTargetRole(announcement.targetRole)
      setPriority(announcement.priority)
      setIsPinned(announcement.isPinned)
      setScheduledAt(convertToLocalDateTimeString(announcement.scheduledAt))
      setExpiresAt(convertToLocalDateTimeString(announcement.expiresAt))
      setEmailNotification(announcement.emailNotification)
    } else {
      resetForm()
    }
  }, [announcement, open])

  const resetForm = () => {
    setTitle('')
    setContent('')
    setSummary('')
    setTargetRole('ALL')
    setPriority('LOW')
    setIsPinned(false)
    setScheduledAt('')
    setExpiresAt('')
    setEmailNotification(false)
    setError('')
  }

  // ローカル時刻文字列をISO文字列に変換（タイムゾーン考慮）
  const convertToISOString = (localDateTimeString: string): string | undefined => {
    if (!localDateTimeString) return undefined
    // datetime-local の値は "YYYY-MM-DDTHH:mm" 形式
    // これをローカルタイムゾーンのDateオブジェクトとして解釈し、ISO文字列に変換
    const date = new Date(localDateTimeString)
    return date.toISOString()
  }

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError(t('form.errorTitle'))
      return
    }
    if (!content.trim()) {
      setError(t('form.errorContent'))
      return
    }

    setLoading(true)
    setError('')

    try {
      if (isEdit && announcement) {
        const updateData: UpdateAnnouncementRequest = {
          title,
          content,
          summary: summary || undefined,
          targetRole,
          priority,
          isPinned,
          scheduledAt: convertToISOString(scheduledAt),
          expiresAt: convertToISOString(expiresAt),
          emailNotification,
        }
        await announcementService.updateAnnouncement(announcement.id, updateData)
      } else {
        const createData: CreateAnnouncementRequest = {
          title,
          content,
          summary: summary || undefined,
          targetRole,
          priority,
          isPinned,
          scheduledAt: convertToISOString(scheduledAt),
          expiresAt: convertToISOString(expiresAt),
          emailNotification,
        }
        await announcementService.createAnnouncement(createData)
      }
      onSubmit()
      onClose()
    } catch (err: any) {
      setError(err.message || t('messages.saveError'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? t('form.editTitle') : t('form.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label={t('form.announcementTitle')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
          />

          <TextField
            label={t('form.summary')}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            fullWidth
            helperText={t('form.summaryHelp')}
          />

          <TextField
            label={t('form.content')}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            multiline
            rows={6}
            fullWidth
            required
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>{t('form.targetRole')}</InputLabel>
              <Select
                value={targetRole}
                label={t('form.targetRole')}
                onChange={(e) => setTargetRole(e.target.value as AnnouncementTargetRole)}
              >
                <MenuItem value="ALL">{t('targetRole.ALL')}</MenuItem>
                <MenuItem value="CORPORATE_ADMIN">{t('targetRole.CORPORATE_ADMIN')}</MenuItem>
                <MenuItem value="USER">{t('targetRole.USER')}</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('form.priority')}</InputLabel>
              <Select
                value={priority}
                label={t('form.priority')}
                onChange={(e) => setPriority(e.target.value as AnnouncementPriority)}
              >
                <MenuItem value="LOW">{t('priority.LOW')}</MenuItem>
                <MenuItem value="HIGH">{t('priority.HIGH')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('form.scheduledAt')}
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label={t('form.expiresAt')}
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={
                <Switch checked={isPinned} onChange={(e) => setIsPinned(e.target.checked)} />
              }
              label={t('form.isPinned')}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={emailNotification}
                  onChange={(e) => setEmailNotification(e.target.checked)}
                />
              }
              label={t('form.emailNotification')}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('common:cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {isEdit ? t('common:save') : t('common:create')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
